// Widgets for the react-app target. Generated by `sui generate` from .sui sources; do not edit.

import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import type { Action, Binding } from "./runtime.js";
import type { BadgeTone } from "./types.js";

function cx(...parts: ReadonlyArray<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** A page: header with title, description and actions above the content. */
export interface PageProps {
  readonly title: string;
  readonly description?: string;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

export function Page({ title, description, actions, children }: PageProps) {
  return (
    <div className="grid gap-6">
      <PageHeader title={title} description={description} actions={actions} />
      {children}
    </div>
  );
}

/** A boxed surface for grouped content, such as a table. */
export interface PanelProps {
  readonly children?: ReactNode;
}

export function Panel({ children }: PanelProps) {
  return <div className="surface overflow-hidden rounded-2xl">{children}</div>;
}

/** An error banner announced to assistive technology. */
export interface NoticeProps {
  readonly children?: ReactNode;
}

export function Notice({ children }: NoticeProps) {
  return (
    <div
      role="alert"
      className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
    >
      {children}
    </div>
  );
}

/** A muted follow-up line inside a notice. */
export interface NoticeHintProps {
  readonly children?: ReactNode;
}

export function NoticeHint({ children }: NoticeHintProps) {
  return <p className="mt-1 text-[var(--muted-foreground)]">{children}</p>;
}

/** Inline code. */
export interface CodeTextProps {
  readonly children?: ReactNode;
}

export function CodeText({ children }: CodeTextProps) {
  return <code>{children}</code>;
}

/** An error line inside a form. */
export interface FormErrorProps {
  readonly children?: ReactNode;
}

export function FormError({ children }: FormErrorProps) {
  return <p role="alert" className="text-sm text-[var(--danger)]">{children}</p>;
}

/** A table: header rows go in `head`, body rows are the children. */
export interface DataTableProps {
  readonly head?: ReactNode;
  readonly children?: ReactNode;
}

export function DataTable({ head, children }: DataTableProps) {
  return (
    <Table>
      <TableHeader>{head}</TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

export interface DataRowProps {
  readonly onPress?: Action;
  readonly children?: ReactNode;
}

export function DataRow({ onPress, children }: DataRowProps) {
  return <TableRow onClick={onPress}>{children}</TableRow>;
}

export interface DataHeadingProps {
  readonly label: string;
}

export function DataHeading({ label }: DataHeadingProps) {
  return <TableHead>{label}</TableHead>;
}

export interface DataCellProps {
  readonly mono?: boolean;
  readonly capitalize?: boolean;
  readonly span?: number;
  readonly children?: ReactNode;
}

export function DataCell({ mono, capitalize, span, children }: DataCellProps) {
  return (
    <TableCell colSpan={span} className={cx(mono && "font-mono text-xs", capitalize && "capitalize")}>
      {children}
    </TableCell>
  );
}

export interface StatusBadgeProps {
  readonly tone?: BadgeTone;
  readonly children?: ReactNode;
}

export function StatusBadge({ tone, children }: StatusBadgeProps) {
  return <Badge tone={(tone ?? "neutral")}>{children}</Badge>;
}

export interface ActionButtonProps {
  readonly label: string;
  readonly onPress: Action;
}

export function ActionButton({ label, onPress }: ActionButtonProps) {
  return <Button type="button" onClick={() => void onPress()}>{label}</Button>;
}

export interface SubmitButtonProps {
  readonly label: string;
  readonly busyLabel?: string;
  readonly busy?: boolean;
}

export function SubmitButton({ label, busyLabel, busy }: SubmitButtonProps) {
  return (
    <Button type="submit" disabled={busy}>
      {busy && busyLabel ? (
        <>
          {busyLabel}
        </>
      ) : (
        <>
          {label}
        </>
      )}
    </Button>
  );
}

/** An edge panel for a focused task; `onClose` runs when it is dismissed. */
export interface SidePanelProps {
  readonly title: string;
  readonly description?: string;
  readonly onClose: Action;
  readonly children?: ReactNode;
}

export function SidePanel({ title, description, onClose, children }: SidePanelProps) {
  return (
    <Drawer
      title={title}
      description={description}
      open
      onOpenChange={(next: boolean) => {
        if (!next) void onClose();
      }}
    >
      {children}
    </Drawer>
  );
}

export interface FormBlockProps {
  readonly onSubmit: Action;
  readonly children?: ReactNode;
}

export function FormBlock({ onSubmit, children }: FormBlockProps) {
  return (
    <form
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void onSubmit();
      }}
      className="grid gap-3"
    >
      {children}
    </form>
  );
}

export interface TextFieldProps {
  readonly label: string;
  readonly id: string;
  readonly bind: Binding<string>;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly autocomplete?: string;
  readonly spellcheck?: boolean;
}

export function TextField({ label, id, bind, placeholder, required, disabled, autocomplete, spellcheck }: TextFieldProps) {
  return (
    <label htmlFor={id} className="grid gap-1 text-xs font-medium">
      {label}
      <Input
        id={id}
        value={bind.value}
        onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => bind.set(event.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autocomplete}
        spellCheck={spellcheck}
      />
    </label>
  );
}

export interface SelectFieldProps {
  readonly label: string;
  readonly id: string;
  readonly bind: Binding<string>;
  readonly disabled?: boolean;
  readonly children?: ReactNode;
}

export function SelectField({ label, id, bind, disabled, children }: SelectFieldProps) {
  return (
    <label htmlFor={id} className="grid gap-1 text-xs font-medium">
      {label}
      <select
        id={id}
        className="select"
        value={bind.value}
        onChange={(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => bind.set(event.target.value)}
        disabled={disabled}
      >
        {children}
      </select>
    </label>
  );
}

export interface OptionItemProps {
  readonly value: string;
}

export function OptionItem({ value }: OptionItemProps) {
  return <option value={value}>{value}</option>;
}
